import type {
  FormulaDoLimiteParaTela,
  SituacaoDaMedicao,
} from '../../types/environmentalMeasurement';

/**
 * Prévia, na tela, da conta do limite da sílica livre (#459). É a segunda cópia da conta: o
 * servidor (`backend/src/services/limiteDaSilica.ts`) é quem decide e grava, e o JSON
 * `docs/specs/459-casos-de-paridade.json` é lido pelos testes dos dois lados para que não divirjam.
 */

/** O nível de ação é esta fração do limite calculado. */
const FRACAO_DO_NIVEL_DE_ACAO = 0.5;

const CASAS_DO_QUARTZO = 2;
const CASAS_DO_LIMITE = 4;

type ParametrosDaConta = Pick<FormulaDoLimiteParaTela, 'numerador' | 'constante'>;

/**
 * Arredonda meio para cima: 12,345 × 100 dá 1234,4999… em ponto flutuante e arredondaria para
 * 12,34 em vez de 12,35, então o produto é aparado a 15 dígitos significativos antes de
 * arredondar. Não passa por texto com expoente, que vira NaN em 5e-7 ou a partir de 1e21.
 */
export function arredondar(numero: number, casas: number): number {
  const fator = 10 ** casas;
  return Math.round(Number((numero * fator).toPrecision(15))) / fator;
}

export function calcularLimiteDaSilica({ numerador, constante }: ParametrosDaConta, quartzo: number): number {
  return arredondar(numerador / (arredondar(quartzo, CASAS_DO_QUARTZO) + constante), CASAS_DO_LIMITE);
}

export function nivelDeAcaoDoLimite(limite: number): number {
  return arredondar(limite * FRACAO_DO_NIVEL_DE_ACAO, CASAS_DO_LIMITE);
}

/** Só `>` estrito ultrapassa: resultado igual ao limite (ou ao nível de ação) fica na faixa de baixo. */
export function situacaoDaMedicao({ valor, limite }: { valor: number; limite: number }): SituacaoDaMedicao {
  if (valor > limite) return 'ACIMA_DO_LIMITE';
  if (valor > nivelDeAcaoDoLimite(limite)) return 'ACIMA_DA_ACAO';
  return 'ABAIXO_DA_ACAO';
}

/** Número da medição como o laudo e o e-mail o escrevem: vírgula decimal e até 4 casas. */
export function formatarNumeroDaMedicao(numero: number): string {
  return numero.toLocaleString('pt-BR', { maximumFractionDigits: CASAS_DO_LIMITE });
}

/** A fórmula por extenso, como o usuário a lê ao lado do limite calculado. */
export function descreverFormula({ numerador, constante }: ParametrosDaConta): string {
  return `${formatarNumeroDaMedicao(numerador)} ÷ (% de quartzo + ${formatarNumeroDaMedicao(constante)})`;
}
